import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../../lib/utils";
import { ChevronDown, X } from "lucide-react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import TooltipWrapper from "../../TooltipWrapper";
import { getTelephonyUsers } from "../../../apis/phoneInventory";

function VirtualTelephonyUserSelect({
    selectedIds = [],
    onChange,
    disabled = false,
    placeholder = "Select users",
    emptyMessage = "No users with dialer access found",
    errorMessage = "Failed to load users",
    className,
    triggerClassName,
    workspaceId,
    preloadedUsers = [], // Users that are already known (e.g., assigned users from API)
}) {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [hasFetched, setHasFetched] = useState(false);

    const parentRef = useRef(null);
    const triggerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const userCacheRef = useRef(new Map()); // Persistent cache of user objects across popover sessions

    // Debounce search query
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setPage(1);
            setMembers([]);
            setHasMore(true);
            setHasFetched(false); // Reset fetch flag when search changes
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchQuery]);

    // Fetch telephony users from API
    const fetchMembers = useCallback(
        async (pageNum, search) => {
            if (!workspaceId) return;

            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            setLoading(true);
            setError(false);

            try {
                const queryParams = new URLSearchParams({
                    page: pageNum.toString(),
                    per_page: "20",
                });

                if (search) {
                    queryParams.append("filter[user_name]", search);
                }

                const response = await getTelephonyUsers({
                    query: queryParams.toString(),
                    options: {
                        headers: { "X-Workspace-Id": workspaceId },
                        signal: abortControllerRef.current.signal,
                    },
                });

                const newMembers = response?.data?.data || response?.data || [];
                const pagination = response?.data?.pagination || {};

                // Cache all fetched users so they persist across popover close/open
                newMembers.forEach((user) => {
                    if (user && user.id) {
                        userCacheRef.current.set(user.id, user);
                    }
                });

                setMembers((prev) => (pageNum === 1 ? newMembers : [...prev, ...newMembers]));
                setHasMore(pagination.current_page < pagination.total_pages || newMembers.length === 20);
                setHasFetched(true); // Mark that we've fetched data
                setLoading(false);
            } catch (err) {
                if (err.name !== "AbortError" && err.name !== "CanceledError") {
                    console.error("Error fetching telephony users:", err);
                    setError(true);
                    setHasFetched(true); // Mark as fetched even on error to prevent retries
                    setLoading(false);
                }
            }
        },
        [workspaceId]
    );

    // Cache preloaded users
    useEffect(() => {
        preloadedUsers.forEach((user) => {
            if (user && user.id) {
                userCacheRef.current.set(user.id, user);
            }
        });
    }, [preloadedUsers]);

    // Fetch members when popover opens or search changes
    useEffect(() => {
        // Only fetch if:
        // 1. Popover is open
        // 2. We haven't already fetched for this search query
        // 3. Not currently loading
        if (open && !hasFetched && !loading) {
            fetchMembers(1, debouncedSearchQuery);
        }
    }, [open, hasFetched, loading, fetchMembers, debouncedSearchQuery]);

    // Load more members when scrolling
    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchMembers(nextPage, debouncedSearchQuery);
        }
    }, [loading, hasMore, page, fetchMembers, debouncedSearchQuery]);

    // Virtual scrolling setup
    const rowVirtualizer = useVirtualizer({
        count: members.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 60,
        overscan: 5,
    });

    // Check if we need to load more when scrolling
    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) return;

        if (lastItem.index >= members.length - 1 && hasMore && !loading) {
            loadMore();
        }
    }, [rowVirtualizer.getVirtualItems(), members.length, hasMore, loading, loadMore]);

    useEffect(() => {
        if (disabled && open) {
            setOpen(false);
        }
    }, [disabled, open]);

    const usersById = useMemo(() => {
        const map = new Map();

        // Start with the persistent cache (survives popover close/open)
        userCacheRef.current.forEach((user, id) => {
            map.set(id, user);
        });

        // Add preloaded users (e.g., assigned users)
        preloadedUsers.forEach((user) => {
            if (user && user.id) {
                map.set(user.id, user);
            }
        });

        // Merge with fetched users (will override if same ID)
        members.forEach((user) => {
            if (user && user.id) {
                map.set(user.id, user);
            }
        });

        return map;
    }, [members, preloadedUsers]);

    const selectedUsers = useMemo(
        () =>
            selectedIds
                .map((id) => {
                    if (usersById.has(id)) {
                        return usersById.get(id);
                    }
                    // Fallback: check persistent cache directly
                    if (userCacheRef.current.has(id)) {
                        return userCacheRef.current.get(id);
                    }
                    // Last resort: show ID (should rarely happen now)
                    return { id, name: id, isPlaceholder: true };
                })
                .filter(Boolean),
        [selectedIds, usersById]
    );

    const toggleUser = (userId) => {
        if (!onChange) return;

        const exists = selectedIds.includes(userId);
        const nextValue = exists ? selectedIds.filter((id) => id !== userId) : [...selectedIds, userId];
        onChange(nextValue);
    };

    const removeUser = (userId) => {
        if (!onChange) return;
        const nextValue = selectedIds.filter((id) => id !== userId);
        onChange(nextValue);
    };

    const handleOpenChange = (nextOpen) => {
        if (disabled) return;
        setOpen(nextOpen);
        if (!nextOpen) {
            setSearchQuery("");
            setHasFetched(false); // Reset fetch flag when closing
        }
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    className={cn(
                        "flex w-full min-h-[40px] items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition focus:outline-none",
                        disabled ? "cursor-not-allowed bg-gray-50 text-gray-400" : "hover:border-[#D0D5DD]",
                        triggerClassName,
                        className
                    )}
                    disabled={disabled}
                >
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                        {selectedUsers.length > 0 ? (
                            selectedUsers.map((user) => (
                                <span
                                    key={user.id}
                                    className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium border-border bg-gray-50 text-gray-700"
                                >
                                    <span>{user.name || user.email || user.id}</span>

                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                removeUser(user.id);
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    )}
                                </span>
                            ))
                        ) : (
                            <span className="text-sm text-gray-500">
                                {loading ? "Loading users..." : placeholder}
                            </span>
                        )}
                    </div>

                    <div className="ml-2 flex items-center gap-2 text-gray-500">
                        {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />}
                        <ChevronDown className="h-4 w-4" />
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0"
                align="start"
                style={{ width: triggerRef.current?.offsetWidth || 320 }}
            >
                <Command shouldFilter={false}>
                    <div className="p-2">
                        <div className="rounded-md border border-border">
                            <CommandInput
                                placeholder="Search users"
                                className="h-9"
                                value={searchQuery}
                                onValueChange={handleSearchChange}
                            />
                        </div>
                    </div>
                    <CommandList>
                        <div
                            ref={parentRef}
                            className="max-h-[300px] overflow-auto"
                        >
                            {error ? (
                                <div className="px-4 py-6 text-sm text-red-500">{errorMessage}</div>
                            ) : members.length === 0 && !loading ? (
                                <CommandEmpty className="py-6 text-center text-sm text-gray-500">
                                    {emptyMessage}
                                </CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    <div
                                        style={{
                                            height: `${rowVirtualizer.getTotalSize()}px`,
                                            width: "100%",
                                            position: "relative",
                                        }}
                                    >
                                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                            const user = members[virtualRow.index];
                                            if (!user || !user.id) return null;

                                            const isSelected = selectedIds.includes(user.id);

                                            return (
                                                <div
                                                    key={user.id}
                                                    data-index={virtualRow.index}
                                                    ref={rowVirtualizer.measureElement}
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        transform: `translateY(${virtualRow.start}px)`,
                                                    }}
                                                    className="flex items-start gap-3 px-3 py-2 hover:bg-accent cursor-pointer"
                                                    onClick={() => toggleUser(user.id)}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() => toggleUser(user.id)}
                                                        onClick={(event) => event.stopPropagation()}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-gray-950">
                                                            {user.name || user.email || user.id}
                                                            {user.note && (
                                                                <span className="ml-2 text-xs text-gray-500">{user.note}</span>
                                                            )}
                                                        </span>
                                                        {user.email && user.name && (
                                                            <span className="text-xs text-gray-500">{user.email}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CommandGroup>
                            )}
                            {loading && members.length > 0 && (
                                <div className="py-3 text-center text-sm text-gray-500">
                                    Loading more...
                                </div>
                            )}
                            {!hasMore && members.length > 0 && !loading && (
                                <div className="py-2 text-center text-xs text-gray-400">
                                    No more users
                                </div>
                            )}
                        </div>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default VirtualTelephonyUserSelect;
